/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Table, Typography, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../authContext';
import { getOperatorSubscriptions, getOverview } from '../services/api';
import {
    formatCount,
    operatorColor,
    paymentStatusTheme,
    PENDING_DEACTIVATION,
    STATUS_ORDER,
    statusTheme,
} from '../utils/theme';
import styles from '../styles/Dashboard.module.css';

const PAGE_SIZES = [25, 50, 100];

const SEARCH_DELAY_MS = 350;
const SEARCH_LABEL = 'Buscar por ICCID u objectId';

/** Filtro de cobro: el valor que entiende el backend y el campo del resumen que lo cuenta. */
const PAYMENT_FILTERS = [
    { value: '', label: 'Todos', countField: 'total' },
    { value: 'apio', label: 'Apio', countField: 'apio' },
    { value: 'stripe', label: 'Stripe', countField: 'stripe' },
    { value: 'none', label: 'Sin credenciales', countField: 'ninguno' },
];

const countFieldFor = (payment) =>
    PAYMENT_FILTERS.find((filter) => filter.value === payment).countField;

const sumRows = (rows, field) => rows.reduce((sum, row) => sum + (row[field] || 0), 0);

const DATE_OPTIONS = {
    timeZone: 'America/Santiago',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('es-CL', DATE_OPTIONS) : '—');

const formatDateTime = (iso) =>
    iso
        ? new Date(iso).toLocaleString('es-CL', {
              ...DATE_OPTIONS,
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
          })
        : '—';

const providerLabel = (provider) => (provider === 'apio' ? 'Apio' : 'Stripe');

function Dot({ color }) {
    return <span className={styles.swatch} style={{ background: color }} />;
}

function CopyableId({ value }) {
    if (!value) return '—';
    return (
        <Typography.Text copyable className={styles.idText}>
            {value}
        </Typography.Text>
    );
}

function FilterChips({ label, legend, options, value, onChange }) {
    return (
        <div className={styles.filterGroup}>
            <p className={styles.filterLabel}>{legend}</p>
            <div className={styles.statusFilters} role="group" aria-label={label}>
                {options.map((option) => (
                    <button
                        key={option.value || 'all'}
                        type="button"
                        aria-pressed={value === option.value}
                        className={
                            value === option.value
                                ? `${styles.filterChip} ${styles.filterChipActive}`
                                : styles.filterChip
                        }
                        onClick={() => onChange(option.value)}
                    >
                        {option.color && <Dot color={option.color} />}
                        {option.label}
                        {option.count != null && (
                            <span className={styles.filterCount}>{formatCount(option.count)}</span>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
}

function PaymentCell({ payment }) {
    if (!payment) return <span className={styles.muted}>Sin cobro</span>;
    const theme = paymentStatusTheme(payment.provider, payment.status);
    return (
        <span className={styles.paymentCell}>
            <span className={styles.providerName}>{providerLabel(payment.provider)}</span>
            <span className={styles.statusCell}>
                <Dot color={theme.color} />
                {theme.label}
            </span>
        </span>
    );
}

const ONE_MINUTE_MS = 60 * 1000;

const sameMinute = (a, b) =>
    Boolean(a && b) && Math.abs(new Date(a) - new Date(b)) < ONE_MINUTE_MS;

/**
 * Hitos de la suscripción en orden. El alta y la activación suelen guardarse en
 * el mismo instante: entonces se muestran como un solo hito. Una terminada suma
 * la baja de la línea, que puede quedar a futuro.
 */
function lifecycleSteps(
    { status, createdAt, activatedAt, terminatedAt, scheduledDeactivationDate },
    now = new Date()
) {
    const steps =
        activatedAt && !sameMinute(createdAt, activatedAt)
            ? [
                  { label: 'Alta', date: createdAt },
                  { label: 'Activación', date: activatedAt },
              ]
            : [{ label: activatedAt ? 'Alta y activación' : 'Alta', date: createdAt || activatedAt }];
    if (terminatedAt || status === 'TERMINATED') {
        steps.push({ label: 'Término', date: terminatedAt });
        if (scheduledDeactivationDate) {
            const upcoming = new Date(scheduledDeactivationDate) > now;
            steps.push({
                label: upcoming ? 'Baja de la línea programada' : 'Baja de la línea',
                date: scheduledDeactivationDate,
                upcoming,
            });
        }
    }
    return steps;
}

function SubscriptionCard({ subscription, project }) {
    const theme = statusTheme(subscription.status);
    const steps = lifecycleSteps(subscription);
    // El hito actual es el último que ya ocurrió: una baja por venir no lo es.
    const current = steps.map((step) => !step.upcoming).lastIndexOf(true);

    return (
        <section
            className={styles.subCard}
            aria-label="Suscripción"
            style={{ '--accent': theme.color }}
        >
            <h3 className={styles.subHeading}>
                <Dot color={theme.color} />
                {theme.label}
            </h3>
            <ol className={styles.lifecycle}>
                {steps.map((step, index) => (
                    <li
                        key={step.label}
                        className={
                            index === current
                                ? `${styles.lifecycleStep} ${styles.lifecycleCurrent}`
                                : styles.lifecycleStep
                        }
                    >
                        <span className={styles.lifecycleLabel}>{step.label}</span>
                        <span className={styles.lifecycleDate}>
                            {step.date ? formatDateTime(step.date) : 'Sin fecha registrada'}
                        </span>
                    </li>
                ))}
            </ol>
            <Link
                className={styles.subLink}
                to={`/sim/dashboard?subObjectId=${encodeURIComponent(subscription.objectId)}`}
                state={{ deviceType: project }}
            >
                Abrir en vista SIM
                <svg className={styles.subLinkChevron} viewBox="0 0 12 12" aria-hidden="true">
                    <path
                        d="M4.5 2.5 8 6 4.5 9.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Link>
        </section>
    );
}

function PaymentCard({ subscription }) {
    const { payment } = subscription;

    if (!payment) {
        const emptyTheme = paymentStatusTheme(null);
        return (
            <section
                className={styles.subCard}
                aria-label="Cobro"
                style={{ '--accent': emptyTheme.color }}
            >
                <div>
                    <h3 className={styles.subHeading}>Sin credenciales de cobro</h3>
                    <p className={styles.subText}>
                        No tiene una suscripción de Apio ni de Stripe enlazada, así que no hay
                        pagos que mostrar.
                    </p>
                </div>
            </section>
        );
    }

    const theme = paymentStatusTheme(payment.provider, payment.status);
    const provider = providerLabel(payment.provider);
    const isApio = payment.provider === 'apio';

    return (
        <section
            className={styles.subCard}
            aria-label="Cobro"
            style={{ '--accent': theme.color }}
        >
            <div>
                <h3 className={styles.subHeading}>
                    <Dot color={theme.color} />
                    {theme.label}
                </h3>
                <p className={styles.subText}>Cobro en {provider}</p>
            </div>
            <dl className={styles.facts}>
                <div>
                    <dt>Suscripción en {provider}</dt>
                    <dd>
                        <CopyableId value={payment.subscriptionId} />
                    </dd>
                </div>
                {isApio && payment.failedAt && (
                    <div>
                        <dt>Último cobro fallido</dt>
                        <dd>{formatDateTime(payment.failedAt)}</dd>
                    </div>
                )}
                {isApio && payment.lastReactivationAttemptAt && (
                    <div>
                        <dt>Último intento de reactivación</dt>
                        <dd>{formatDateTime(payment.lastReactivationAttemptAt)}</dd>
                    </div>
                )}
            </dl>
        </section>
    );
}

/** Lo que se ve al abrir una fila: la vida de la suscripción y su cobro según Parse. */
function SubscriptionDetail({ subscription, project }) {
    return (
        <div className={styles.subDetail}>
            <SubscriptionCard subscription={subscription} project={project} />
            <PaymentCard subscription={subscription} />
        </div>
    );
}

const COLUMNS = [
    {
        title: 'ICCID',
        dataIndex: 'iccId',
        key: 'iccId',
        render: (value) => <CopyableId value={value} />,
    },
    {
        title: 'Estado',
        dataIndex: 'status',
        key: 'status',
        render: (value) => {
            const theme = statusTheme(value);
            return (
                <span className={styles.statusCell}>
                    <Dot color={theme.color} />
                    {theme.label}
                </span>
            );
        },
    },
    {
        title: 'Cobro',
        dataIndex: 'payment',
        key: 'payment',
        render: (payment) => <PaymentCell payment={payment} />,
    },
    {
        title: 'Alta',
        key: 'activatedAt',
        render: (_, record) => formatDate(record.activatedAt || record.createdAt),
    },
    {
        title: 'ObjectId',
        dataIndex: 'objectId',
        key: 'objectId',
        render: (value) => <CopyableId value={value} />,
    },
];

/** Con el filtro de baja programada, la fecha de baja reemplaza al alta: es lo que se viene a mirar. */
const columnsFor = (status) =>
    status === PENDING_DEACTIVATION
        ? COLUMNS.map((column) =>
              column.key === 'activatedAt'
                  ? {
                        title: 'Baja programada',
                        key: 'scheduledDeactivationDate',
                        render: (_, record) => formatDate(record.scheduledDeactivationDate),
                    }
                  : column
          )
        : COLUMNS;

/**
 * Lista completa de suscripciones de un operador en un producto. El filtro, la
 * búsqueda y la paginación se resuelven en el backend: ENTEL tiene miles.
 *
 * El filtro de estado puede venir de afuera (`status` y `onStatusChange`), para
 * que las tarjetas de KPIs lo fijen; si no llega, la lista lo maneja sola.
 */
export default function OperatorSubscriptions({
    project,
    operatorName,
    status: controlledStatus,
    onStatusChange,
}) {
    const { tokens } = useAuth();
    const [messageApi, contextHolder] = message.useMessage();
    const [internalStatus, setInternalStatus] = useState('');
    const isStatusControlled = controlledStatus !== undefined;
    const status = isStatusControlled ? controlledStatus : internalStatus;
    const [payment, setPayment] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [operatorRows, setOperatorRows] = useState(null);
    const latestRequest = useRef(0);
    // Conteos del operador por estado y cobro: salen del resumen, que el backend tiene en cache.
    useEffect(() => {
        if (!tokens?.AccessToken) return undefined;
        let cancelled = false;
        getOverview(tokens.AccessToken, project)
            .then((overview) => {
                if (!cancelled) {
                    setOperatorRows(
                        overview.rows.filter((row) => row.mnoProviderName === operatorName)
                    );
                }
            })
            .catch(() => {
                if (!cancelled) setOperatorRows([]);
            });
        return () => {
            cancelled = true;
        };
    }, [tokens?.AccessToken, project, operatorName]);

    // Otro filtro, otra lista: se vuelve a la primera página.
    useEffect(() => {
        setPage(1);
    }, [status]);

    // Espera a que el agente deje de escribir antes de buscar.
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, SEARCH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        if (!tokens?.AccessToken) return;
        const requestId = latestRequest.current + 1;
        latestRequest.current = requestId;
        setLoading(true);
        getOperatorSubscriptions(tokens.AccessToken, project, operatorName, {
            status,
            payment,
            search,
            page,
            pageSize,
        })
            .then((result) => {
                if (latestRequest.current === requestId) setData(result);
            })
            .catch((error) => {
                if (latestRequest.current !== requestId) return;
                messageApi.error(
                    error.response?.data?.message ||
                        'No se pudo cargar la lista de suscripciones'
                );
            })
            .finally(() => {
                if (latestRequest.current === requestId) setLoading(false);
            });
    }, [
        tokens?.AccessToken,
        project,
        operatorName,
        status,
        payment,
        search,
        page,
        pageSize,
        messageApi,
    ]);

    // Cada grupo cuenta respetando el filtro del otro: con "Sin credenciales"
    // activo, los estados cuentan solo las que no tienen credenciales.
    const statusOptions = useMemo(() => {
        // Llega desde la tarjeta de Movimiento: no es un estado del resumen y no tiene conteo aquí.
        const pendingOption =
            status === PENDING_DEACTIVATION
                ? [
                      {
                          value: PENDING_DEACTIVATION,
                          label: statusTheme(PENDING_DEACTIVATION).label,
                          color: statusTheme(PENDING_DEACTIVATION).color,
                          count: data?.total ?? null,
                      },
                  ]
                : [];
        if (!operatorRows) return [{ value: '', label: 'Todas', count: null }, ...pendingOption];
        const field = countFieldFor(payment);
        const present = STATUS_ORDER.filter((value) =>
            operatorRows.some((row) => row.status === value && row.total > 0)
        );
        return [
            { value: '', label: 'Todas', count: sumRows(operatorRows, field) },
            ...present.map((value) => ({
                value,
                label: statusTheme(value).label,
                color: statusTheme(value).color,
                count: sumRows(
                    operatorRows.filter((row) => row.status === value),
                    field
                ),
            })),
            ...pendingOption,
        ];
    }, [operatorRows, payment, status, data?.total]);

    const paymentOptions = useMemo(() => {
        // El resumen no sabe cuáles tienen baja programada: sin conteo por cobro.
        const rows =
            status === PENDING_DEACTIVATION
                ? null
                : operatorRows?.filter((row) => !status || row.status === status);
        return PAYMENT_FILTERS.map((filter) => ({
            value: filter.value,
            label: filter.label,
            count: rows ? sumRows(rows, filter.countField) : null,
        }));
    }, [operatorRows, status]);

    const emptyText = search
        ? `No hay suscripciones de ${operatorName} que coincidan con “${search}”.`
        : `No hay suscripciones de ${operatorName} con estos filtros.`;

    return (
        <section
            className={`${styles.tile} ${styles.listTile}`}
            style={{ '--operator': operatorColor(operatorName) }}
        >
            {contextHolder}
            <div className={styles.listToolbar}>
                <div className={styles.filterGroups}>
                    <FilterChips
                        label="Filtrar por estado"
                        legend="Estado"
                        options={statusOptions}
                        value={status}
                        onChange={(value) => {
                            if (!isStatusControlled) setInternalStatus(value);
                            if (onStatusChange) onStatusChange(value);
                            setPage(1);
                        }}
                    />
                    <FilterChips
                        label="Filtrar por cobro"
                        legend="Cobro"
                        options={paymentOptions}
                        value={payment}
                        onChange={(value) => {
                            setPayment(value);
                            setPage(1);
                        }}
                    />
                </div>
                <Input
                    allowClear
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder={SEARCH_LABEL}
                    aria-label={SEARCH_LABEL}
                    prefix={<SearchOutlined />}
                    className={styles.searchInput}
                />
            </div>

            <Table
                className={`${styles.table} ${styles.listTable}`}
                rowKey="objectId"
                columns={columnsFor(status)}
                dataSource={data?.items || []}
                loading={loading}
                size="middle"
                scroll={{ x: 900 }}
                expandable={{
                    expandedRowRender: (record) => (
                        <SubscriptionDetail
                            project={project}
                            subscription={record}
                        />
                    ),
                }}
                locale={{ emptyText }}
                pagination={{
                    current: page,
                    pageSize,
                    total: data?.total || 0,
                    pageSizeOptions: PAGE_SIZES,
                    showSizeChanger: true,
                    showTotal: (total, [from, to]) =>
                        `${formatCount(from)}–${formatCount(to)} de ${formatCount(total)}`,
                    onChange: (nextPage, nextPageSize) => {
                        if (nextPageSize !== pageSize) {
                            setPageSize(nextPageSize);
                            setPage(1);
                        } else {
                            setPage(nextPage);
                        }
                    },
                }}
            />
        </section>
    );
}
